/**
 * EditServiceProviderConnectionDialog — first-class edit surface for a
 * service-provider connection stored under `serviceProviderConnections` in
 * `connections.json` (or the draft file in draft mode).
 *
 * Manifest-driven UI: reuses `ManifestConnectionForm` and
 * `useManifestFormState` so the auth / display-name affordances match
 * `EditConnectionDialog` (managed / ARM). The two dialogs share the form
 * body; the ingress + egress paths differ:
 *
 *   - Ingress: `connection` prop (already a `ServiceProviderConnection` from
 *     the local record) + fetched connector manifest via
 *     `useServiceProviderConnector` + `buildConnectorManifest`. No ARM
 *     round-trip.
 *   - Egress: on Save, run `extractServiceProviderAppSettings` to move any
 *     `AppConfiguration`-sourced leaves (secrets, connection strings) into
 *     app settings — `@appsetting('NAME')` references — before writing back.
 *     Then upsert app settings via `useUpsertEnvironmentSettings` and route
 *     the updated `ServiceProviderConnection` through the caller-supplied
 *     `onSave` (typically `handleSaveLocalConnection`), keeping draft/prod
 *     routing authoritative.
 *
 * Scope notes:
 * - OAuth section only for a connection whose credential comes from a separate
 *   OAuth connection. Its Sign in button re-authenticates that backing
 *   connection, not this record, which has no credential of its own.
 * - No preflight / test-connection on Save. If validation is needed, users
 *   can save and rerun the workflow — mirrors the read-only behavior of the
 *   MCP + agent edit dialogs in this PR.
 * - Secret fields round-trip losslessly: if the initial value is
 *   `@appsetting('NAME')`, `extractServiceProviderAppSettings` treats that
 *   as already-referenced and leaves both the ref and the underlying
 *   app-setting value untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, tokens } from '@coreai/fluentui-react';
import { useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useEffectiveManifestWithFallback } from '@/hooks/useEffectiveManifest';
import { useOAuthConnection, type OAuthConnectionResult } from '@/hooks/useOAuthConnection';
import {
  useEnvironmentSettings,
  useUpsertEnvironmentSettings,
} from '@/hooks/useEnvironmentSettings';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  buildConnectorManifest,
  getConnectionParameterSets,
  hasRealParameterSets,
} from '@/services/manifestConnectionService';
import {
  extractServiceProviderAppSettings,
  hasAppSettingReferences,
  resolveAppSettingReferences,
} from '@/utils/connections/serviceProviderAppSettings';
import { isOAuthConnectionParameterSet, getOAuthConnectionParameterKey } from '@/utils/connections';
import type { FormValues } from '@/types/connectionParameters';
import type { ServiceProviderConnection } from '@/types/connections';
import { ManifestConnectionForm } from './editForm/ManifestConnectionForm';
import { useManifestFormState } from './editForm/useManifestFormState';
import { useDiscardGuard } from './editForm/useDiscardGuard';
import { ConnectionEditDialogShell } from './editForm/ConnectionEditDialogShell';

const useStyles = makeStyles({
  dialogSurface: {
    minHeight: '480px',
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface EditServiceProviderConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Key under `serviceProviderConnections` in connections.json. */
  connectionKey: string;
  /** The local connection record being edited. */
  connection: ServiceProviderConnection;
  /**
   * Called with the updated record. Routes through
   * `useConnectionsEditor.handleSaveLocalConnection(id, 'serviceProvider', record)`
   * so draft/prod mode is honored.
   */
  onSave: (recordKey: string, record: ServiceProviderConnection) => Promise<void>;
  /**
   * Whether the Connections view's Draft/Production toggle is on Draft. Threaded into
   * env-settings read/write so `@appsetting()` references resolve against — and
   * secrets are written to — the matching slot.
   */
  isDraftMode: boolean;
}

export const EditServiceProviderConnectionDialog = ({
  open,
  onClose,
  connectionKey,
  connection,
  onSave,
  isDraftMode,
}: EditServiceProviderConnectionDialogProps) => {
  const styles = useStyles();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const selectedProject = useAppStore((state) => state.selectedProject);
  const selectedAppName = useAppStore((state) => state.selectedAppName);
  const { data: prodSettings } = useEnvironmentSettings(
    selectedProject ?? '',
    selectedAppName ?? '',
    /* isDraftMode */ false,
  );
  // Safe with empty project/app: the mutation short-circuits when there
  // are no additions, and we guard the .mutateAsync call below.
  const upsertAppSettings = useUpsertEnvironmentSettings(
    selectedProject ?? '',
    selectedAppName ?? '',
    isDraftMode,
    prodSettings ?? [],
  );

  // Pull the app's environment settings so we can resolve any
  // `@appsetting('NAME')` references embedded in `parameterValues` back to
  // their real values before seeding the form. Without this, the user would
  // see literal reference strings in secret fields (e.g. the connection
  // string / account key) — surprising and un-editable in place. The query
  // is disabled when project/app aren't selected; the seed effect below
  // gracefully falls back to raw values in that case.
  const {
    data: envSettings,
    isLoading: isLoadingEnvSettings,
  } = useEnvironmentSettings(selectedProject ?? '', selectedAppName ?? '', isDraftMode);
  const envSettingsMap = useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    (envSettings ?? []).forEach((s) => {
      map[s.name] = s.value;
    });
    return map;
  }, [envSettings]);

  // Fetch manifest sources. SPs are often "unfetchable" via the ARM-style
  // `useConnectorMetadata`, but `useServiceProviderConnector` pulls the
  // fuller runtime-side definition. `buildConnectorManifest` prefers the
  // latter and falls back to the former's trimmed embedded copy if needed.
  const connectorId = connection.serviceProvider.id;
  const { data: fetchedMetadata, isLoading: isLoadingMetadata } =
    useConnectorMetadata(connectorId);
  const { data: serviceProviderConnector, isLoading: isLoadingSpConnector } =
    useServiceProviderConnector(connectorId ?? null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest: null,
      }),
    [connectorId, serviceProviderConnector],
  );
  const effectiveManifest = useEffectiveManifestWithFallback(
    connectorManifest,
    fetchedMetadata,
  );

  const parameterSets = useMemo(
    () => getConnectionParameterSets(effectiveManifest),
    [effectiveManifest],
  );
  const isRealMultiAuth = useMemo(
    () => hasRealParameterSets(effectiveManifest),
    [effectiveManifest],
  );

  const form = useManifestFormState();
  const { seed } = form;
  // Close/discard orchestration shared with the other ConnectionList edit
  // dialogs. Busy-guard mirrors the original: closes are blocked while the
  // secret-upsert mutation is in flight. `resetToOriginal` runs on discard.
  const discardGuard = useDiscardGuard({
    isDirty: form.hasChanges,
    isBusy: upsertAppSettings.isPending,
    onClose,
    onDiscard: form.resetToOriginal,
  });
  // Stable reference for the seed effect below; the guard object itself
  // re-identifies each render, but `keepEditing` is a stable callback.
  const { keepEditing: resetDiscardConfirm } = discardGuard;
  const [saveError, setSaveError] = useState<string | null>(null);

  // Detect whether we need env-settings before we can seed. If the record
  // has no `@appsetting()` refs, we can seed immediately from `connection`.
  // If it does but project/app aren't selected (query disabled), we still
  // seed with the raw refs rather than blocking forever — the user will see
  // the reference string and can wire secrets elsewhere.
  const rawParameterValues = useMemo(
    () => (connection.parameterValues ?? {}) as Record<string, unknown>,
    [connection.parameterValues],
  );
  const needsResolution = useMemo(
    () => hasAppSettingReferences(rawParameterValues),
    [rawParameterValues],
  );
  const canFetchEnvSettings = !!selectedProject && !!selectedAppName;
  const isWaitingForEnvSettings =
    needsResolution && canFetchEnvSettings && envSettings === undefined;

  // Seed once per open+record combination. Depending on the stable `seed`
  // callback (not the whole `form` object, which re-identifies every render)
  // is not enough on its own: `envSettings` also changes identity when
  // `useUpsertEnvironmentSettings` writes back via `setQueryData` on
  // success. Without a guard, a partial-success save (secrets upserted,
  // then `onSave` throws) would re-run this effect after the mutation,
  // wipe the user's local edits, and disable the Save button — leaving
  // them unable to retry. The ref tracks the connection key we've
  // successfully seeded so subsequent runs are no-ops until the dialog
  // closes or a different record is opened.
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededKeyRef.current = null;
      return;
    }
    if (seededKeyRef.current === connectionKey) return;
    if (isWaitingForEnvSettings) return;
    const resolvedValues =
      needsResolution && envSettings !== undefined
        ? resolveAppSettingReferences(rawParameterValues, envSettingsMap)
        : rawParameterValues;
    seed({
      displayName: connection.displayName ?? connectionKey,
      // `connection.parameterValues` is `Record<string, unknown>` from the
      // connections.json schema. Widen to `FormValues` for the shared form
      // state — DynamicConnectionForm will only touch fields declared by the
      // parameter set, so any non-`FormFieldValue` leaves are inert.
      formValues: resolvedValues as FormValues,
      selectedParameterSetName: connection.parameterSetName,
    });
    seededKeyRef.current = connectionKey;
    resetDiscardConfirm();
    setSaveError(null);
  }, [
    open,
    connection,
    connectionKey,
    seed,
    isWaitingForEnvSettings,
    needsResolution,
    envSettings,
    envSettingsMap,
    rawParameterValues,
    resetDiscardConfirm,
  ]);

  const selectedParameterSet =
    form.selectedParameterSetName ?? parameterSets[0]?.name ?? '';
  const currentParameterSet = useMemo(
    () =>
      parameterSets.find((s) => s.name === selectedParameterSet) ?? parameterSets[0],
    [parameterSets, selectedParameterSet],
  );

  const isLoading =
    isLoadingMetadata ||
    isLoadingSpConnector ||
    (needsResolution && canFetchEnvSettings && isLoadingEnvSettings);

  // The credential belongs to a separate OAuth connection, so the offer follows the
  // selected parameter set rather than the saved record.
  const supportsOAuthConnection = isOAuthConnectionParameterSet(currentParameterSet);
  const oauthParameterKey = useMemo(
    () => getOAuthConnectionParameterKey(currentParameterSet),
    [currentParameterSet],
  );

  const {
    createOAuthConnection,
    state: oauthState,
    reset: resetOAuth,
  } = useOAuthConnection();

  const isSigningIn =
    oauthState === 'creating' ||
    oauthState === 'authenticating' ||
    oauthState === 'confirming' ||
    oauthState === 'syncing';

  useEffect(() => {
    if (!open) resetOAuth();
  }, [open, resetOAuth]);

  const [isSaving, setIsSaving] = useState(false);

  const persist = useCallback(async (values: FormValues) => {
    setSaveError(null);
    if (!form.displayName.trim()) {
      setSaveError('Connection name is required');
      return;
    }

    setIsSaving(true);
    // Tracks whether we've handed off to `onSave` (the JSON-save layer).
    // Once that call starts, the shared `mutateActiveConnections` handler
    // owns the success + error notifications for the write to
    // connections.json, so we must NOT double-notify from here — matches
    // how the agent + MCP dialogs behave (they don't notify at all).
    let onSaveInvoked = false;
    try {
      // Move `AppConfiguration`-sourced leaves (secrets etc.) into app
      // settings, replacing the inline value with an `@appsetting(NAME)`
      // reference. Untouched refs pass through unchanged.
      const { parameterValues: storedParamValues, appSettings } =
        extractServiceProviderAppSettings(
          connectionKey,
          values,
          currentParameterSet,
        );

      if (Object.keys(appSettings).length > 0) {
        if (!selectedProject || !selectedAppName) {
          throw new Error(
            'No environment/app context available — cannot persist secret app settings.',
          );
        }
        await upsertAppSettings.mutateAsync(appSettings);
      }

      const updated: ServiceProviderConnection = {
        ...connection,
        displayName: form.displayName.trim(),
        parameterValues: storedParamValues,
        ...(form.selectedParameterSetName
          ? { parameterSetName: form.selectedParameterSetName }
          : {}),
      };

      onSaveInvoked = true;
      await onSave(connectionKey, updated);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update connection';
      setSaveError(message);
      // Only surface a notification for failures that happened BEFORE the
      // JSON save layer took over — validation, missing project/app, or
      // the secret-upsert step. Errors from `onSave` itself are already
      // notified by `mutateActiveConnections` in `useConnectionsEditor`.
      if (!onSaveInvoked) {
        addNotification({
          type: 'error',
          title: 'Failed to update connection',
          message,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    form.displayName,
    form.selectedParameterSetName,
    connection,
    connectionKey,
    currentParameterSet,
    onSave,
    onClose,
    addNotification,
    upsertAppSettings,
    selectedProject,
    selectedAppName,
  ]);

  const handleSave = useCallback(
    () => persist(form.formValues),
    [persist, form.formValues],
  );

  // Sign in replaces Save for OAuth-connection sets. The backing connection is named after
  // this key, so the create call overwrites an existing one rather than adding a second.
  const handleSignIn = useCallback(async () => {
    if (!supportsOAuthConnection) return;

    // The hook reports handled failures through `error`, but transport failures reject.
    let result: OAuthConnectionResult;
    try {
      result = await createOAuthConnection({
        connectorId,
        connectionName: connectionKey,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Sign in failed',
        message:
          error instanceof Error ? error.message : 'Authorization was cancelled or failed.',
      });
      return;
    }

    if (!result.connection) {
      addNotification({
        type: 'error',
        title: 'Sign in failed',
        message: result.error ?? 'Authorization was cancelled or failed.',
      });
      return;
    }

    await persist({ ...form.formValues, [oauthParameterKey]: result.connection.id });
  }, [
    supportsOAuthConnection,
    oauthParameterKey,
    createOAuthConnection,
    connectorId,
    connectionKey,
    addNotification,
    persist,
    form.formValues,
  ]);

  return (
    <ConnectionEditDialogShell
      open={open}
      title="Edit service provider connection"
      discardGuard={discardGuard}
      isBusy={isSaving || isSigningIn}
      // Sign in is an action rather than an edit, so it stays available on a pristine form —
      // but it writes the record too, so it needs a name to write.
      canSave={
        supportsOAuthConnection
          ? !isLoading && form.displayName.trim().length > 0
          : form.hasChanges
      }
      onSave={supportsOAuthConnection ? handleSignIn : handleSave}
      {...(supportsOAuthConnection
        ? { primaryLabel: 'Sign in', primaryBusyLabel: 'Signing in…' }
        : {})}
      surfaceClassName={styles.dialogSurface}
    >
      <ManifestConnectionForm
        displayName={form.displayName}
        onDisplayNameChange={form.setDisplayName}
        displayNamePlaceholder={connectionKey}
        displayNameTestId="edit-sp-connection-display-name"
        parameterSets={parameterSets}
        selectedParameterSet={selectedParameterSet}
        onParameterSetChange={form.setSelectedParameterSetName}
        values={form.formValues}
        onValuesChange={form.setFormValues}
        showAuthTypeSelector={isRealMultiAuth && parameterSets.length > 1}
        disabled={isSaving || isSigningIn}
        isLoading={isLoading}
      />
      {saveError && (
        <div className={styles.errorBanner} role="alert">
          {saveError}
        </div>
      )}
    </ConnectionEditDialogShell>
  );
};

EditServiceProviderConnectionDialog.displayName =
  'EditServiceProviderConnectionDialog';
